import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import {
  Users,
  Calendar,
  TrendingUp,
  BarChart3,
  Trophy
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getOrganizationStatistics, OrganizationStats } from '@/lib/firebase/queries/organizations/statistics';
import { ChartTooltip } from '@/components/ui/chart';
import { INDICATORS } from '@/lib/assessment/namatAssessment';
import { share } from '@/lib/assessment/orgClinicalStats';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

interface ManagerStatisticsProps {
  organizationId: string;
}

export function ManagerStatistics({ organizationId }: ManagerStatisticsProps) {
  const { t, dir } = useLanguage();
  const [stats, setStats] = useState<OrganizationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (organizationId) {
      fetchStatistics();
    }
  }, [organizationId]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const organizationStats = await getOrganizationStatistics(organizationId);
      setStats(organizationStats);
    } catch (err) {
      console.error('Failed to fetch organization statistics:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">Error Loading Statistics</h3>
            <p className="text-muted-foreground">
              {error || 'Unable to load organization statistics.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const monthlyTrendsData = stats.monthlyTrends.map(trend => ({
    month: trend.month,
    newUsers: trend.users,
    completedExams: trend.exams,
    avgScore: trend.avgScore
  }));

  const healthDistributionData = [
    { name: t('dashboard.manager.statistics.excellent'), value: stats.healthDistribution.excellent, color: '#7dda55' },
    { name: t('dashboard.manager.statistics.veryGood'), value: stats.healthDistribution.veryGood, color: '#7dda55' },
    { name: t('dashboard.manager.statistics.good'), value: stats.healthDistribution.good, color: '#004250' },
    { name: t('dashboard.manager.statistics.needsImprovement'), value: stats.healthDistribution.needsImprovement, color: '#004250' }
  ];

  const habitCategoryData = Object.entries(stats.habitLogsByCategory).map(([category, value]) => ({
    category,
    logs: value
  }));


  return (
    <div className="space-y-4" dir={dir}>
      {/* Top Row: Key Metrics */}
      <div className={`grid gap-4 grid-cols-4 ${dir === 'rtl' ? 'rtl' : ''}`}>
        <Card className="p-6 shadow-sm border border-[#004250]/20 rounded-lg bg-white">
          <div className={`flex items-center gap-4 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <Users className="h-8 w-8 text-[#004250]" />
            <div className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              <div className="text-sm font-medium text-[#004250]">{t('dashboard.manager.statistics.totalCustomers')}</div>
              <div className="text-3xl font-bold text-[#004250]">{stats.totalCustomers}</div>
            </div>
          </div>
        </Card>

        <Card className="p-6 shadow-sm border border-[#004250]/20 rounded-lg bg-white">
          <div className={`flex items-center gap-4 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <TrendingUp className="h-8 w-8 text-[#7dda55]" />
            <div className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              <div className="text-sm font-medium text-[#7dda55]">{t('dashboard.manager.statistics.activeCustomers')}</div>
              <div className="text-3xl font-bold text-[#004250]">{stats.activeCustomers}</div>
            </div>
          </div>
        </Card>

        <Card className="p-6 shadow-sm border border-[#004250]/20 rounded-lg bg-white">
          <div className={`flex items-center gap-4 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <Calendar className="h-8 w-8 text-[#004250]" />
            <div className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              <div className="text-sm font-medium text-[#004250]">{t('dashboard.manager.statistics.examDoneShare')}</div>
              <div className="text-3xl font-bold text-[#004250]" data-testid="exam-done-share">
                {share(stats.clinical.examDone, stats.clinical.beneficiaries)}%
              </div>
              <div className="text-xs text-[#004250]/70">
                {stats.clinical.examDone} / {stats.clinical.beneficiaries}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 shadow-sm border border-[#004250]/20 rounded-lg bg-white">
          <div className={`flex items-center gap-4 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <Trophy className="h-8 w-8 text-[#7dda55]" />
            <div className={dir === 'rtl' ? 'text-right' : 'text-left'}>
              <div className="text-sm font-medium text-[#7dda55]">{t('dashboard.manager.statistics.averageNamatScore')}</div>
              <div className="text-3xl font-bold text-[#004250]">
                {stats.clinical.averagePercent === null ? '—' : `${stats.clinical.averagePercent}%`}
              </div>
              <div className="text-xs text-[#004250]/70" data-testid="scale-done-share">
                {t('dashboard.manager.statistics.scaleDoneShare', {
                  percent: share(stats.clinical.scaleDone, stats.clinical.beneficiaries),
                  count: stats.clinical.scaleDone,
                })}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Second Row: Engagement Metrics */}
      <div className={`grid gap-4 grid-cols-3 ${dir === 'rtl' ? 'rtl' : ''}`}>
        <Card className="p-6 shadow-sm border border-[#004250]/20 rounded-lg bg-white">
          <div className={`text-sm font-medium text-[#004250] mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.totalCompetitionPoints')}</div>
          <div className={`text-4xl font-bold text-[#004250] mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{stats.totalCompetitionPoints}</div>
          <div className={`text-sm text-[#004250]/70 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
            {stats.totalCustomers > 0 ? Math.round(stats.totalCompetitionPoints / stats.totalCustomers) : 0} {t('dashboard.manager.statistics.avgPerCustomer')}
          </div>
        </Card>

        <Card className="p-6 shadow-sm border border-[#004250]/20 rounded-lg bg-white">
          <div className={`text-sm font-medium text-[#7dda55] mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.pendingExams')}</div>
          <div className={`text-4xl font-bold text-[#7dda55] mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{stats.pendingExams}</div>
          <div className={`text-sm text-[#7dda55]/70 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.needCompletion')}</div>
        </Card>

        <Card className="p-6 shadow-sm border border-[#004250]/20 rounded-lg bg-white">
          <div className={`text-sm font-medium text-[#7dda55] mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.activeInLast30Days')}</div>
          <div className={`text-4xl font-bold text-[#7dda55] mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{stats.activeCustomers}</div>
          <div className={`text-sm text-[#004250]/70 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
            {stats.totalCustomers > 0 ? Math.round((stats.activeCustomers / stats.totalCustomers) * 100) : 0}% {t('dashboard.manager.statistics.ofCustomers')}
          </div>
        </Card>
      </div>

      {/* Third Row: Charts */}
      <div className={`grid gap-6 grid-cols-2 ${dir === 'rtl' ? 'rtl' : ''}`}>
        {/* Health Score Distribution */}
        <Card className="p-6">
          <h3 className={`text-lg font-semibold mb-6 text-[#004250] ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.healthScoreDistribution')}</h3>
          <div className="h-[320px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={healthDistributionData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => 
                    percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                  }
                  labelLine={false}
                  fontSize={12}
                >
                  {healthDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className={`flex flex-wrap gap-4 justify-center mt-4 text-sm ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#7dda55] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.excellent')}</span>
            </div>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#7dda55] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.veryGood')}</span>
            </div>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#004250] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.good')}</span>
            </div>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#004250] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.needsImprovement')}</span>
            </div>
          </div>
        </Card>

        {/* Healthy share per indicator, of those measured (#95) */}
        <Card className="p-6">
          <h3 className={`text-lg font-semibold mb-1 text-[#004250] ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.healthyByIndicator')}</h3>
          <p className={`text-xs text-[#004250]/70 mb-4 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.ofMeasured')}</p>
          <div className="h-[300px]" data-testid="healthy-by-indicator">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={INDICATORS.filter((key) => stats.clinical.indicators[key].measured > 0).map((key) => ({
                  section: t(`common.health.indicators.${key}`),
                  score: share(stats.clinical.indicators[key].healthy, stats.clinical.indicators[key].measured),
                  measured: stats.clinical.indicators[key].measured,
                }))}
                margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
              >
                <XAxis dataKey="section" tick={{ fontSize: 13 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 14 }} unit="%" />
                <ChartTooltip />
                <Bar dataKey="score" fill="#7dda55" radius={[6, 6, 0, 0]} label={{ position: 'top', formatter: (v: number) => `${v}%` }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Fourth Row: More Charts */}
      <div className={`grid gap-6 grid-cols-2 ${dir === 'rtl' ? 'rtl' : ''}`}>
        {/* Habit Building Distribution */}
        <Card className="p-6">
          <h3 className={`text-lg font-semibold mb-6 text-[#004250] ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.habitBuildingDistribution')}</h3>
          <div className="h-[320px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: t('dashboard.manager.statistics.consistent'), value: stats.habitBuildingDistribution.consistent, color: '#7dda55' },
                    { name: t('dashboard.manager.statistics.regular'), value: stats.habitBuildingDistribution.regular, color: '#004250' },
                    { name: t('dashboard.manager.statistics.occasional'), value: stats.habitBuildingDistribution.occasional, color: '#7dda55' },
                    { name: t('dashboard.manager.statistics.struggling'), value: stats.habitBuildingDistribution.struggling, color: '#004250' }
                  ]}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  dataKey="value"
                  label={({ name, percent }) => 
                    percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''
                  }
                  labelLine={false}
                  fontSize={12}
                >
                  {[
                    { color: '#7dda55' },
                    { color: '#004250' },
                    { color: '#7dda55' },
                    { color: '#004250' }
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <ChartTooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className={`flex flex-wrap gap-4 justify-center mt-4 text-sm ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#7dda55] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.consistent')}</span>
            </div>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#004250] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.regular')}</span>
            </div>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#7dda55] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.occasional')}</span>
            </div>
            <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <div className="w-3 h-3 bg-[#004250] rounded"></div>
              <span className="text-[#004250]">{t('dashboard.manager.statistics.struggling')}</span>
            </div>
          </div>
        </Card>

        {/* Habit Logs by Category */}
        <Card className="p-6">
          <h3 className={`text-lg font-semibold mb-6 text-[#004250] ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.habitLogsByCategory')}</h3>
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={habitCategoryData}
                margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
              >
                <XAxis dataKey="category" tick={{ fontSize: 14 }} />
                <YAxis tick={{ fontSize: 14 }} />
                <ChartTooltip />
                <Bar dataKey="logs" fill="#7dda55" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Bottom Row: Monthly Trends and Summary */}
      <div className={`grid gap-6 grid-cols-2 ${dir === 'rtl' ? 'rtl' : ''}`}>
        {/* Monthly Trends */}
        <Card className="p-6">
          <h3 className={`text-lg font-semibold mb-6 text-[#004250] ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.monthlyTrends')}</h3>
          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={monthlyTrendsData}
                margin={{ top: 30, right: 30, left: 20, bottom: 30 }}
              >
                <XAxis dataKey="month" tick={{ fontSize: 14 }} />
                <YAxis tick={{ fontSize: 14 }} />
                <ChartTooltip />
                <Line 
                  type="monotone" 
                  dataKey="newUsers" 
                  stroke="#7dda55" 
                  strokeWidth={3}
                  dot={{ fill: '#7dda55', strokeWidth: 2, r: 5 }}
                  name={t('dashboard.manager.statistics.newUsers')} 
                />
                <Line 
                  type="monotone" 
                  dataKey="completedExams" 
                  stroke="#004250" 
                  strokeWidth={3}
                  dot={{ fill: '#004250', strokeWidth: 2, r: 5 }}
                  name={t('dashboard.manager.statistics.examsTaken')} 
                />
                <Line 
                  type="monotone" 
                  dataKey="avgScore" 
                  stroke="#7dda55" 
                  strokeWidth={3}
                  dot={{ fill: '#7dda55', strokeWidth: 2, r: 5 }}
                  name={t('dashboard.manager.statistics.avgScore')} 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Engagement Metrics Summary */}
        <Card className="p-6">
          <h3 className={`text-lg font-semibold mb-6 text-[#004250] ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.engagementMetrics')}</h3>
          <div className="space-y-6">
            <div className={`flex justify-between items-center py-3 border-b border-[#004250]/20 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <span className={`text-sm font-medium text-[#004250]/70 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.totalHabitLogs')}</span>
              <span className={`text-2xl font-bold text-[#004250] ${dir === 'rtl' ? 'text-left' : 'text-right'}`}>{stats.engagementMetrics.totalHabitLogs}</span>
            </div>
            <div className={`flex justify-between items-center py-3 border-b border-[#004250]/20 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <span className={`text-sm font-medium text-[#004250]/70 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.avgPointsPerCustomer')}</span>
              <span className={`text-2xl font-bold text-[#7dda55] ${dir === 'rtl' ? 'text-left' : 'text-right'}`}>{stats.engagementMetrics.averagePointsPerCustomer}</span>
            </div>
            <div className={`flex justify-between items-center py-3 border-b border-[#004250]/20 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
              <span className={`text-sm font-medium text-[#004250]/70 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.activityRate')}</span>
              <span className={`text-2xl font-bold text-[#7dda55] ${dir === 'rtl' ? 'text-left' : 'text-right'}`}>
                {stats.totalCustomers > 0 ? Math.round((stats.activeCustomers / stats.totalCustomers) * 100) : 0}%
              </span>
            </div>
            <div className={`mt-8 pt-6 text-center ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
              <div className={`text-sm text-[#004250]/70 mb-3 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.overallOrganizationHealthScore')}</div>
              <div className={`text-5xl font-bold text-[#004250] mb-2 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{stats.averageNamatScore}%</div>
              <div className={`text-sm text-[#7dda55] font-semibold ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>{t('dashboard.manager.statistics.needsImprovement')}</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}